import { Box, Grid, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  InputTabFormValues,
  saveInputData,
  updateModel,
  useInputTabStyles,
} from './input-util'
import { ScheduleHoursRow, ScheduleMonthsRow } from './InputScheduleComponents'
import { theme } from '../../../../theme'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { validators } from '../../../../validation/validators'
import { powerUnit } from '../../../../util/formatters'

type Props = {
  id: string
  schedules: ScheduleFragment[]
  defaultValues: InputTabFormValues | undefined
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputElectricityTab({
  id,
  schedules,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const classes = useInputTabStyles()
  const { t } = useTranslation()

  const { control, errors, reset, formState } = useForm<InputTabFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const dbUpdateNeededRef = useRef(false)

  const updateModelData = async (values: InputTabFormValues) => {
    await updateModel({
      values,
      defaultValues: defaultValues ?? ({} as InputTabFormValues),
      esType: EnergySystemType.ElectricityConsumption,
      control,
      formState,
      updateStateRef: dbUpdateNeededRef,
      onReset: reset,
      onUpdate,
    })
  }

  const saveChanges = async () => {
    await saveInputData({ energySystemId: id, control, onDbUpdate })
  }

  useEffect(() => {
    void updateModelData(defaultValues ?? ({} as InputTabFormValues))

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      <Box className={classes.inputTab}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="values.parameters.electConsumption.electricityBaseLoad"
                  error={
                    errors.values?.parameters?.electConsumption
                      ?.electricityBaseLoad
                  }
                  label={t(
                    'energySystem.parameters.electConsumption.electBaseLoad',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  size="small"
                  InputProps={{
                    endAdornment: <Typography>{powerUnit}</Typography>,
                  }}
                  onBlur={() => updateModelData(control.getValues())}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="values.scheduleId"
                  error={errors.values?.scheduleId}
                  label={t(
                    'energySystem.parameters.electConsumption.baseLoadUsageProfile',
                  )}
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
            </Grid>

            <ScheduleHoursRow
              control={control}
              errors={errors}
              onUpdateModel={updateModelData}
            />

            <ScheduleMonthsRow
              control={control}
              errors={errors}
              onUpdateModel={updateModelData}
            />
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}
